// skillgenpro-frontend/src/error-framework/fetchWrapper.js
//
// Payment (services/razorpay.js) uses the browser's native `fetch`, not
// axios — so it never passes through axiosInterceptor.js. This wrapper
// gives raw-fetch call sites the same normalized-error behavior the axios
// interceptor gives everything else, WITHOUT changing their calling
// convention: it still resolves with parsed JSON on success and still
// throws on failure, exactly like the `fetch(...).then(r => r.json())`
// pattern already used in razorpay.js — the only difference is that what
// gets thrown is now a NormalizedError instead of a bare `new Error(...)`.

import { normalizeError } from './errorMapper';

/**
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {Object} [context] - e.g. { feature: 'checkout' } — merged into
 *   the normalized error so ErrorExperience can show the right copy
 */
export async function fetchWithErrorHandling(url, options, context) {
  let response;
  try {
    response = await fetch(url, options);
  } catch (networkError) {
    // fetch() itself only throws for network-layer failures (offline, DNS,
    // CORS block, etc.) — never for HTTP error status codes.
    throw normalizeError(networkError, context);
  }

  if (!response.ok) {
    let data = null;
    try {
      data = await response.json();
    } catch (parseErr) {
      // Body wasn't JSON (e.g. a raw 502 from API Gateway before any Lambda
      // response body was even formed) — fall through with data = null,
      // errorMapper's classifyRawError still has the status code to work with.
    }
    const shaped = { response: { status: response.status, data } };
    throw normalizeError(shaped, context);
  }

  try {
    return await response.json();
  } catch (parseErr) {
    // A 2xx with an unparseable body is itself an unexpected-shape failure.
    throw normalizeError({ response: { status: response.status, data: null } }, context);
  }
}
